/**
 * Pure helpers for the dynamic plugin config form.
 */
import { CONFIG_GROUP_ORDER, ConfigGroup } from '@insio/shared-types';
import { validateUrl } from '@insio/validation';
import { isPlainObject, omit, sortBy } from 'es-toolkit';
import { mappingKeysOf, withoutMappingKeys, withoutMappingValues } from './codelist-offer';
import type { DiscoveredChoice } from './use-discovered-defaults';
import { effectiveRequired, visibleKeys } from './config-visibility';
import type { ConfigFormValues, ConfigSchema, ConfigSchemaProperty, LocalizedText } from '../types';

/**
 * The half of a config schema one row owns.
 *
 * A property with no `scope` reads as `instance`, matching the SDK's own default. The backend's
 * `config-schema.util.ts` composes these two the same way under the same names, and the two must
 * stay in step: that one decides what is accepted, this one decides what is shown.
 */
export function scopeSchema(schema: ConfigSchema, scope: 'connection' | 'instance'): ConfigSchema {
  return pickProperties(schema, property => (property.scope ?? 'instance') === scope);
}

/**
 * True when at least one property is connection-scoped — what "this plugin needs a shared
 * connection" means for a direct connector, which has no bridge to imply one. The backend's
 * `hasConnectionScope` asks the same of the same property, and the two must stay in step.
 */
export function hasConnectionScope(schema: ConfigSchema): boolean {
  return Object.values(schema.properties ?? {}).some(property => property.scope === 'connection');
}

/** The part of a schema one entity type sees; a property naming none applies to all of them. */
export function forEntityType(schema: ConfigSchema, entityType: string): ConfigSchema {
  return pickProperties(
    schema,
    property => property.entityTypes === undefined || property.entityTypes.includes(entityType)
  );
}

/** What one connector configures: its own scope, narrowed to its entity. */
export function instanceSchemaFor(schema: ConfigSchema, entityType: string): ConfigSchema {
  return forEntityType(scopeSchema(schema, 'instance'), entityType);
}

function pickProperties(
  schema: ConfigSchema,
  keep: (property: ConfigSchemaProperty, key: string) => boolean
): ConfigSchema {
  const kept = Object.fromEntries(
    Object.entries(schema.properties ?? {}).filter(([key, property]) => keep(property, key))
  );
  return {
    ...schema,
    properties: kept,
    required: (schema.required ?? []).filter(key => key in kept)
  };
}

/**
 * The schema as a form shows it for `configValues`: the settings whose condition holds, with the
 * condition resolved, and `required` narrowed to them. A schema without conditions is itself.
 */
export function shownSchema(
  schema: ConfigSchema,
  configValues: Record<string, unknown>
): ConfigSchema {
  const properties = schema.properties ?? {};
  const conditioned = Object.values(properties).some(
    property => isPlainObject(property) && property.visibleWhen != null
  );
  if (!conditioned) return schema;

  const shown = Object.fromEntries(
    visibleKeys(schema, configValues).flatMap(key => {
      const property = properties[key];
      return property ? [[key, omit(property, ['visibleWhen'])]] : [];
    })
  );
  return { ...schema, properties: shown, required: effectiveRequired(schema, configValues) };
}

/** Field kind resolved from a schema property. */
export type ConfigFieldKind = 'string' | 'enum' | 'number' | 'boolean' | 'secret' | 'json';

/** Resolves how a schema property should be rendered. */
export function getFieldKind(property: ConfigSchemaProperty): ConfigFieldKind {
  if (property.secret === true) return 'secret';
  if (property.enum && property.enum.length > 0) return 'enum';
  if (property.type === 'number' || property.type === 'integer') return 'number';
  if (property.type === 'boolean') return 'boolean';
  if (property.type === 'string') return 'string';
  return 'json';
}

/** Builds initial non-secret config values from schema defaults. */
export function getDefaultConfigValues(schema: ConfigSchema): Record<string, unknown> {
  const values: Record<string, unknown> = {};
  for (const [key, property] of Object.entries(schema.properties ?? {})) {
    if (property.default === undefined || property.secret === true) continue;
    const kind = getFieldKind(property);
    values[key] =
      kind === 'json' ? JSON.stringify(property.default) : (property.default as unknown);
  }
  return values;
}

/** Config key → the value a bridge found on the customer's machine. */
export type DiscoveredSuggestions = Record<string, { value: string; source: string }>;

export interface ApplyDiscoveredOptions {
  /** A value that is still the schema default may be replaced: nobody chose it. */
  overDefaults?: boolean;
  /** Keys the reader set themselves; a default among them counts as chosen. */
  touched?: ReadonlySet<string>;
}

/** A discovered text as the value its setting holds; undefined when the setting cannot take it. */
export function discoveredValueFor(
  property: ConfigSchemaProperty | undefined,
  value: string
): unknown {
  if (!property || property.secret === true) return undefined;
  switch (getFieldKind(property)) {
    case 'enum':
      return (property.enum ?? []).find(option => String(option) === value);
    case 'boolean':
      return value === 'true' ? true : value === 'false' ? false : undefined;
    case 'number': {
      const number = value.trim() === '' ? Number.NaN : Number(value);
      const fits = property.type === 'integer' ? Number.isInteger(number) : Number.isFinite(number);
      return fits ? number : undefined;
    }
    case 'string':
      return value;
    default:
      return undefined;
  }
}

/**
 * Whether a field holds this discovered text, as the value its setting would store: a number
 * found as `5000.0` is held by `5000`. A text no setting can take is compared as it is.
 */
export function holdsDiscoveredValue(
  property: ConfigSchemaProperty | undefined,
  current: unknown,
  text: string | undefined
): boolean {
  if (current === undefined || current === null || text === undefined) return false;
  const value = discoveredValueFor(property, text);
  if (typeof value === 'number') {
    return String(current).trim() !== '' && Number(current) === value;
  }
  return String(current) === (value === undefined ? text : String(value));
}

/** Whether a field holds exactly this text, whatever type it is stored as. */
function holdsText(current: unknown, text: string | undefined): boolean {
  return current !== undefined && current !== null && String(current) === text;
}

/**
 * Fills fields from what the bridge found, never over a value the person put there: a field is
 * filled while empty, while it holds the `previous` suggestion, or — asked to — an unchosen default.
 */
export function applyDiscoveredDefaults(
  schema: ConfigSchema,
  configValues: Record<string, unknown>,
  suggestions: DiscoveredSuggestions,
  previous: DiscoveredSuggestions = {},
  options: ApplyDiscoveredOptions = {}
): Record<string, unknown> {
  const applied = { ...configValues };
  let changed = false;

  for (const [key, suggestion] of Object.entries(suggestions)) {
    const property = schema.properties?.[key];
    const value = discoveredValueFor(property, suggestion.value);
    if (!property || value === undefined) continue;

    const current = applied[key];
    const isEmpty =
      current === undefined ||
      current === null ||
      (typeof current === 'string' && current.trim() === '');
    const unchosenDefault =
      options.overDefaults === true &&
      property.default !== undefined &&
      holdsText(current, String(property.default)) &&
      options.touched?.has(key) !== true;
    if (
      !isEmpty &&
      !holdsDiscoveredValue(property, current, previous[key]?.value) &&
      !unchosenDefault
    ) {
      continue;
    }
    if (holdsDiscoveredValue(property, current, suggestion.value)) continue;

    applied[key] = value;
    changed = true;
  }

  // The original reference when nothing was filled, so a caller can skip a pointless update.
  return changed ? applied : configValues;
}

/**
 * Empties every field still holding what an earlier pick put there, unless `kept` fills it
 * again — so a new pick never leaves half of the old one behind. A changed field stays.
 */
export function withoutPicked(
  configValues: Record<string, unknown>,
  picked: DiscoveredSuggestions,
  kept: DiscoveredSuggestions = {},
  schema: ConfigSchema = {}
): Record<string, unknown> {
  const stale = Object.entries(picked).filter(
    ([key, found]) =>
      !(key in kept) &&
      holdsDiscoveredValue(schema.properties?.[key], configValues[key], found.value)
  );
  if (stale.length === 0) return configValues;
  const cleared = { ...configValues };
  for (const [key] of stale) delete cleared[key];
  return cleared;
}

/** True when a discovered value can fill this property: known, no secret or JSON, and of its type. */
export function acceptsDiscoveredValue(
  property: ConfigSchemaProperty | undefined,
  value: string
): boolean {
  return discoveredValueFor(property, value) !== undefined;
}

/** The suggestions this schema's fields can take; the rest are dropped. */
export function acceptedSuggestions(
  schema: ConfigSchema,
  suggestions: DiscoveredSuggestions
): DiscoveredSuggestions {
  return Object.fromEntries(
    Object.entries(suggestions).filter(([key, found]) =>
      acceptsDiscoveredValue(schema.properties?.[key], found.value)
    )
  );
}

/**
 * The keys {@link applyDiscoveredDefaults} would leave alone because the person put another value
 * there — what has to be confirmed before a pick replaces it.
 */
export function discoveredConflicts(
  schema: ConfigSchema,
  configValues: Record<string, unknown>,
  suggestions: DiscoveredSuggestions,
  previous: DiscoveredSuggestions = {}
): string[] {
  const applied = applyDiscoveredDefaults(schema, configValues, suggestions, previous);
  return Object.entries(suggestions)
    .filter(([key, found]) => acceptsDiscoveredValue(schema.properties?.[key], found.value))
    .filter(
      ([key, found]) => !holdsDiscoveredValue(schema.properties?.[key], applied[key], found.value)
    )
    .map(([key]) => key);
}

/** What a form offers of the things found: plain suggestions, and the choices to pick from. */
export interface DiscoveredOffer<Choice> {
  suggestions: DiscoveredSuggestions;
  pickable: Choice[];
}

/**
 * A one-option choice reads as suggestions unless a field it fills holds another value; it then
 * stays a pick. Codelists and mapping-key values are the mapper's, never offered here.
 */
export function discoveredOfferOf(
  schema: ConfigSchema,
  configValues: Record<string, unknown>,
  suggestions: DiscoveredSuggestions,
  choices: readonly DiscoveredChoice[]
): DiscoveredOffer<DiscoveredChoice> {
  let offered = withoutMappingKeys(schema, suggestions);
  const pickable: DiscoveredChoice[] = [];
  const mappingKeys = mappingKeysOf(schema);
  // A snapshot is not partitioned: a pick of it must not fill a mapping row either.
  const offeredChoices = choices.flatMap(choice => {
    const kept = withoutMappingValues(choice, mappingKeys);
    return kept ? [kept] : [];
  });
  for (const choice of offeredChoices) {
    const [only] = choice.options;
    if (choice.options.length !== 1 || !only) {
      if (choice.options.length >= 2) pickable.push(choice);
      continue;
    }
    const values = acceptedSuggestions(schema, only.values);
    const inTheWay = Object.entries(values).some(([key, found]) => {
      const current = configValues[key];
      const empty = current === undefined || current === null || String(current).trim() === '';
      return !empty && !holdsDiscoveredValue(schema.properties?.[key], current, found.value);
    });
    if (!inTheWay) continue;
    pickable.push(choice);
    offered = Object.fromEntries(
      Object.entries(offered).filter(([key, found]) => values[key]?.value !== found.value)
    );
  }
  return { suggestions: offered, pickable };
}

/** Converts instance configValues into editable form values (json fields as text). */
export function toEditableConfigValues(
  schema: ConfigSchema,
  configValues: Record<string, unknown>
): Record<string, unknown> {
  const values: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(configValues ?? {})) {
    const property = schema.properties?.[key];
    const kind = property ? getFieldKind(property) : 'json';
    values[key] = kind === 'json' && value !== undefined ? JSON.stringify(value) : value;
  }
  return values;
}

/** The error of JSON text that does not parse, which no save can send. */
export const INVALID_JSON_ERROR = 'errors.invalidJson';

/** The error of a number setting holding no number, which a save would send as `null`. */
export const INVALID_NUMBER_ERROR = 'errors.invalidNumber';

export interface ValidateConfigFormOptions {
  /** Skips required-field checks so an incomplete draft can still be saved. */
  partial?: boolean;
}

/**
 * Validates form values against the schema; a key its condition hides is neither required nor
 * checked. Returns field key → error translation key (relative to the integrations feature).
 */
export function validateConfigForm(
  schema: ConfigSchema,
  configValues: Record<string, unknown>,
  secrets: Record<string, string>,
  clearSecrets: string[],
  secretsSet: string[],
  options: ValidateConfigFormOptions = {}
): Record<string, string> {
  const errors: Record<string, string> = {};
  const shown = shownSchema(schema, configValues);
  const required = new Set(options.partial ? [] : (shown.required ?? []));

  for (const [key, property] of Object.entries(shown.properties ?? {})) {
    const kind = getFieldKind(property);

    if (kind === 'secret') {
      const hasStored = secretsSet.includes(key) && !clearSecrets.includes(key);
      const hasNewValue = (secrets[key] ?? '').length > 0;
      if (required.has(key) && !hasStored && !hasNewValue) {
        errors[key] = 'errors.fieldRequired';
      }
      continue;
    }

    const value = configValues[key];
    const isEmpty =
      value === undefined || value === null || (typeof value === 'string' && value.trim() === '');

    if (isEmpty) {
      if (required.has(key) && kind !== 'boolean') {
        errors[key] = 'errors.fieldRequired';
      }
      continue;
    }

    if (kind === 'string' && typeof value === 'string') {
      if (property.format === 'uri' && !validateUrl(value).isValid) {
        errors[key] = 'errors.invalidUrl';
      } else if (property.minLength !== undefined && value.length < property.minLength) {
        errors[key] = 'errors.tooShort';
      } else if (property.maxLength !== undefined && value.length > property.maxLength) {
        errors[key] = 'errors.tooLong';
      }
    } else if (kind === 'number') {
      const num = typeof value === 'number' ? value : Number(value);
      if (Number.isNaN(num)) {
        errors[key] = INVALID_NUMBER_ERROR;
      } else if (property.minimum !== undefined && num < property.minimum) {
        errors[key] = 'errors.numberTooSmall';
      } else if (property.maximum !== undefined && num > property.maximum) {
        errors[key] = 'errors.numberTooLarge';
      }
    } else if (kind === 'json' && typeof value === 'string') {
      try {
        JSON.parse(value);
      } catch {
        errors[key] = INVALID_JSON_ERROR;
      }
    }
  }

  // A hidden JSON setting is still parsed when the values are saved, so its text has to parse.
  for (const [key, property] of Object.entries(schema.properties ?? {})) {
    const value = configValues[key];
    if (Object.hasOwn(shown.properties ?? {}, key) || getFieldKind(property) !== 'json') continue;
    if (typeof value === 'string' && value.trim() !== '' && !parsesAsJson(value)) {
      errors[key] = INVALID_JSON_ERROR;
    }
  }

  return errors;
}

function parsesAsJson(text: string): boolean {
  try {
    JSON.parse(text);
    return true;
  } catch {
    return false;
  }
}

/**
 * Converts editable form values into GraphQL-ready configValues (numbers/json parsed). A hidden
 * number that is no number is sent as it is held, never as `null`.
 */
export function normalizeConfigValues(
  schema: ConfigSchema,
  configValues: Record<string, unknown>
): Record<string, unknown> {
  const normalized: Record<string, unknown> = {};
  const shown = shownSchema(schema, configValues).properties ?? {};
  for (const [key, value] of Object.entries(configValues)) {
    const property = schema.properties?.[key];
    if (!property || property.secret === true) continue;
    const isEmpty =
      value === undefined || value === null || (typeof value === 'string' && value.trim() === '');
    if (isEmpty) continue;

    const kind = getFieldKind(property);
    if (kind === 'number') {
      const number = typeof value === 'number' ? value : Number(value);
      normalized[key] = Number.isNaN(number) && !Object.hasOwn(shown, key) ? value : number;
    } else if (kind === 'json' && typeof value === 'string') {
      normalized[key] = JSON.parse(value);
    } else {
      normalized[key] = value;
    }
  }
  return normalized;
}

/** Words a config key abbreviates, spelled the way a person would read them. */
const KEY_WORDS: Record<string, string> = {
  api: 'API',
  db: 'database',
  dir: 'directory',
  exe: 'executable',
  ico: 'IČO',
  id: 'ID',
  ms: '(ms)',
  ttl: 'TTL',
  url: 'URL',
  vat: 'VAT'
};

/** A config key as a label: `monS3ExePath` → "Mon S3 executable path", `timeoutMs` → "Timeout (ms)". */
export function labelOf(key: string): string {
  const words = key
    .replace(/[_-]+/g, ' ')
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2')
    .split(/\s+/)
    .filter(Boolean)
    .map(word => {
      const lower = word.toLowerCase();
      if (KEY_WORDS[lower]) return KEY_WORDS[lower];
      // A token with a digit is a name (S3, V2): keep it as written.
      return /\d/.test(word) ? word : lower;
    });
  const [first, ...rest] = words;
  if (!first) return key;
  const head = KEY_WORDS[first.toLowerCase()]
    ? first
    : first.charAt(0).toUpperCase() + first.slice(1);
  return [head, ...rest].join(' ');
}

/** The language a plugin-declared text falls back to when the UI language has none. */
const FALLBACK_TEXT_LANGUAGE = 'en';

/** A plugin-declared text in the UI language, else in English; `undefined` when neither is set. */
export function localizedTextOf(
  text: LocalizedText | undefined,
  language: string | undefined
): string | undefined {
  if (!text || typeof text !== 'object') return undefined;
  const nonBlank = (value: unknown) =>
    typeof value === 'string' && value.trim() !== '' ? value : undefined;
  return nonBlank(language ? text[language] : undefined) ?? nonBlank(text[FALLBACK_TEXT_LANGUAGE]);
}

/** A field's label: the plugin's own in the UI language, else the humanized key. */
export function fieldLabelOf(
  key: string,
  property: ConfigSchemaProperty,
  language: string | undefined
): string {
  return localizedTextOf(property.label, language) ?? labelOf(key);
}

/** A field's hint: the plugin's own in the UI language; the description while the label falls back. */
export function fieldHintOf(
  property: ConfigSchemaProperty,
  language: string | undefined
): string | undefined {
  if (localizedTextOf(property.label, language) === undefined) return property.description;
  return localizedTextOf(property.hint, language);
}

/** An enum option's name: the plugin's own in the UI language, else in English, else the value. */
export function enumOptionLabelOf(
  property: ConfigSchemaProperty,
  option: string,
  language: string | undefined
): string {
  const labels: unknown = property.enumLabels;
  if (!isPlainObject(labels) || !Object.hasOwn(labels, option)) return option;
  return localizedTextOf(labels[option] as LocalizedText | undefined, language) ?? option;
}

/** The default of a property as the form shows it next to the field; `null` when it has none. */
export function defaultLabelOf(property: ConfigSchemaProperty): string | null {
  if (property.default === undefined || property.default === null) return null;
  return typeof property.default === 'object'
    ? JSON.stringify(property.default)
    : String(property.default);
}

export interface ConfigSchemaGroups {
  /** What must be filled in before anything works. */
  required: ConfigSchema;
  /** Worth a look, but nothing depends on it. */
  optional: ConfigSchema;
  /** Numbers with a default the plugin chose — timeouts, intervals — that rarely need changing. */
  advanced: ConfigSchema;
}

/** Splits a schema into what needs attention first, what is worth a look, and what can wait. */
export function groupConfigSchema(schema: ConfigSchema): ConfigSchemaGroups {
  const required = new Set(schema.required ?? []);
  const isAdvanced = (property: ConfigSchemaProperty) =>
    getFieldKind(property) === 'number' && property.default !== undefined;
  return {
    required: pickProperties(schema, (_, key) => required.has(key)),
    optional: pickProperties(
      schema,
      (property, key) => !required.has(key) && !isAdvanced(property)
    ),
    advanced: pickProperties(schema, (property, key) => !required.has(key) && isAdvanced(property))
  };
}

/** One catalogue group of a schema, holding the settings a form shows under its title. */
export interface ConfigGroupSection {
  group: ConfigGroup;
  schema: ConfigSchema;
}

/** The group stored data falls into when it names none, or one this build does not know. */
const FALLBACK_GROUP = ConfigGroup.ADVANCED;

function isCatalogueGroup(value: unknown): value is ConfigGroup {
  return CONFIG_GROUP_ORDER.some(group => group === value);
}

/** Whether the plugin lays its settings out in catalogue groups; one known group is enough. */
export function declaresConfigGroups(schema: ConfigSchema): boolean {
  return Object.values(schema.properties ?? {}).some(property => isCatalogueGroup(property.group));
}

/**
 * The schema split by group, in catalogue order, each group's settings by `order`; a group
 * holding nothing is left out. A setting with no known group goes to the last one.
 */
export function configGroupSections(schema: ConfigSchema): ConfigGroupSection[] {
  const groupOf = (property: ConfigSchemaProperty) =>
    isCatalogueGroup(property.group) ? property.group : FALLBACK_GROUP;
  const positionOf = ({ order }: ConfigSchemaProperty) =>
    typeof order === 'number' && Number.isFinite(order) ? order : Number.POSITIVE_INFINITY;
  const required = schema.required ?? [];

  return CONFIG_GROUP_ORDER.flatMap(group => {
    const members = Object.entries(schema.properties ?? {}).filter(
      ([, property]) => groupOf(property) === group
    );
    if (members.length === 0) return [];
    // Stable, so equal positions keep the order the plugin declared them in.
    const properties = Object.fromEntries(
      sortBy(members, [([, property]) => positionOf(property)])
    );
    return [
      {
        group,
        schema: {
          ...schema,
          properties,
          required: required.filter(key => Object.hasOwn(properties, key))
        }
      }
    ];
  });
}

/**
 * The setting to focus for `key`: the key itself while it is shown, the nearest shown setting
 * that controls it while a condition hides it, and null for a key nothing here can show.
 */
export function focusTargetOf(
  schema: ConfigSchema,
  values: ConfigFormValues,
  key: string
): string | null {
  const properties = schema.properties ?? {};
  const shown = new Set(visibleKeys(schema, values.configValues));
  const walked = new Set<string>();
  let current = key;
  while (Object.hasOwn(properties, current) && !walked.has(current)) {
    if (shown.has(current)) return current;
    walked.add(current);
    const controller: unknown = properties[current]?.visibleWhen?.key;
    if (typeof controller !== 'string') return null;
    current = controller;
  }
  return null;
}
